import React from 'react';
import { graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Pagination } from 'swiper/modules';
import 'swiper/css/pagination';
import styled, { css } from 'styled-components';
import {
  brandColours,
  fontSize,
  minBreakpointQuery,
  standardColours,
  standardTransition,
} from '../styles';
import { Slider } from './ui';

const StyledContentImageSlider = styled.section`
  margin-bottom: 20px;

  ${minBreakpointQuery.medium`
    width: 50%;
  `}

  ${minBreakpointQuery.large`
    width: 60%;
  `}

  ${({ $flip }) => {
    if ($flip) {
      return css`
        ${minBreakpointQuery.medium`
          margin-right: 30px;
          float: left;
        `}

        ${minBreakpointQuery.xlarge`
          margin-right: 40px;
          margin-left: -30px;
        `}

        ${minBreakpointQuery.xxlarge`
          margin-right: 50px;
          margin-left: -60px;
        `}

        ${minBreakpointQuery.xxxlarge`
          margin-right: 60px;
          margin-left: -90px;
        `}

        ${minBreakpointQuery.xxxxlarge`
          margin-right: 70px;
          margin-left: -120px;
        `}
      `;
    } else {
      return css`
        ${minBreakpointQuery.medium`
          margin-left: 30px;
          float: right;
        `}

        ${minBreakpointQuery.xlarge`
          margin-right: -30px;
          margin-left: 40px;
        `}

        ${minBreakpointQuery.xxlarge`
          margin-right: -60px;
          margin-left: 50px;
        `}

        ${minBreakpointQuery.xxxlarge`
          margin-right: -90px;
          margin-left: 60px;
        `}

        ${minBreakpointQuery.xxxxlarge`
          margin-right: -120px;
          margin-left: 70px;
        `}
      `;
    }
  }}
`;

const StyledSlider = styled(Slider)`
  padding-bottom: 40px;
  overflow: visible;

  .swiper-pagination {
    --swiper-pagination-bottom: 0;
    left: 50%;
    display: inline-flex;
    padding: 8px;
    width: auto;
    background-color: ${standardColours.lightGrey};
    border-radius: 40px;
    transform: translateX(-50%);
  }

  .swiper-pagination-bullet {
    --swiper-pagination-bullet-horizontal-gap: 3px;
    --swiper-pagination-bullet-inactive-color: ${brandColours.primary};
    --swiper-pagination-bullet-inactive-opacity: 1;
    transition: ${standardTransition('background-color')};
  }

  .swiper-pagination-bullet-active {
    --swiper-pagination-color: ${standardColours.darkGrey};
  }

  ${({ $flip }) => {
    if ($flip) {
      return css`
        clip-path: inset(0 0 0 -100vw);
      `;
    } else {
      return css`
        clip-path: inset(0 -100vw 0 0);
      `;
    }
  }}
`;

const StyledFigure = styled.figure`
  margin: 0;
`;

const StyledCaption = styled.figcaption`
  margin-top: 12px;
  ${fontSize(14)};
  text-align: left;

  ${minBreakpointQuery.small`
    margin-top: 14px;
    ${fontSize(16)};
  `}

  ${minBreakpointQuery.large`
    margin-top: 16px;
    ${fontSize(18)};
  `}
`;

const ContentImageSlider = ({ images, flip }) => {
  const sliderOptions = {
    modules: [Pagination],
    slidesPerView: 1,
    spaceBetween: 20,
    pagination: { clickable: true },
    dir: flip ? 'rtl' : 'ltr',
  };

  return (
    <StyledContentImageSlider $flip={flip}>
      <StyledSlider {...sliderOptions} $flip={flip}>
        {images.map(({ gatsbyImageData, title, description }, id) => (
          <StyledFigure key={id}>
            <GatsbyImage image={gatsbyImageData} alt={title} />
            <StyledCaption>{description}</StyledCaption>
          </StyledFigure>
        ))}
      </StyledSlider>
    </StyledContentImageSlider>
  );
};

export default ContentImageSlider;

export const ContentImageSliderImageFragment = graphql`
  fragment ContentImageSliderImageFragment on ContentfulAsset {
    gatsbyImageData(width: 810, height: 570)
    title
    description
  }
`;
